import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DrugDetailSheet from "@/components/DrugDetailSheet";
import TrendIndicator from "@/components/TrendIndicator";
import { formatGbp, formatRelative } from "@/lib/format";
import { Briefcase, Sparkles, Clock } from "lucide-react";

const STAGE_BADGE = {
    "Preclinical": "bg-[#F1F5F9] text-[#475569]",
    "Phase I": "bg-[#F1F5F9] text-[#475569]",
    "Phase II": "bg-[#FEF3C7] text-[#92400E]",
    "Phase III": "bg-[#FED7AA] text-[#9A3412]",
    "Pre-registration": "bg-[#FFEDD5] text-[#C2410C]",
    "Approved": "bg-[#DCFCE7] text-[#166534]",
    "Marketed": "bg-[#1A4331] text-white",
};

export default function MyQueuePage() {
    const { user } = useAuth();
    const [drugs, setDrugs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeId, setActiveId] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/my-assignments")
            .then((r) => setDrugs(r.data))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="p-6 lg:p-10 max-w-6xl">
            <div className="mb-8 fade-in-up">
                <p className="text-xs uppercase tracking-widest text-[#8A9A86]">Your queue</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#1A4331] mt-2">
                    {user?.full_name?.split(" ")[0]}&apos;s opportunities
                </h1>
                <p className="text-[#475569] mt-3 max-w-2xl">
                    Drugs assigned to you by the sales administrator. Tap any row for full intelligence, evidence and product recommendations.
                </p>
            </div>

            {loading ? (
                <Card className="p-10 bg-white border-[#E2E8F0] text-center text-[#8A9A86]">Loading your queue…</Card>
            ) : drugs.length === 0 ? (
                <Card className="p-14 bg-white border-[#E2E8F0] text-center fade-in-up stagger-1">
                    <div className="w-14 h-14 rounded-full bg-[#F1F5F9] mx-auto flex items-center justify-center">
                        <Briefcase className="w-6 h-6 text-[#8A9A86]" />
                    </div>
                    <h3 className="font-heading text-xl font-medium text-[#1A4331] mt-4">No opportunities yet</h3>
                    <p className="text-sm text-[#475569] mt-2 max-w-sm mx-auto">
                        Your sales administrator will assign drugs here. Check back soon.
                    </p>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-queue-grid">
                    {drugs.map((d) => (
                        <Card key={d.id} onClick={() => setActiveId(d.id)}
                            className="p-5 bg-white border-[#E2E8F0] cursor-pointer card-lift fade-in-up"
                            data-testid={`queue-card-${d.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <p className="font-medium text-[#1E293B]">{d.name}</p>
                                    <p className="text-xs text-[#475569] mt-0.5">{d.company}</p>
                                </div>
                                <Badge className={`${STAGE_BADGE[d.development_stage]} border-0`}>
                                    {d.development_stage}
                                </Badge>
                            </div>
                            <p className="text-xs text-[#8A9A86] mt-3 uppercase tracking-wider">{d.clinical_area}</p>
                            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#E2E8F0]">
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-[#8A9A86]">Med Aff £</p>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="font-mono text-base font-semibold text-[#1A4331]">
                                            {formatGbp(d.medical_affairs_spend_gbp)}
                                        </span>
                                        <TrendIndicator trend={d.medical_affairs_trend} />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-[#8A9A86]">Promo £</p>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <span className="font-mono text-base font-semibold text-[#C0604A]">
                                            {formatGbp(d.promotion_spend_gbp)}
                                        </span>
                                        <TrendIndicator trend={d.promotion_trend} />
                                    </div>
                                </div>
                                {!d.intelligence_generated && (
                                    <Badge className="ml-auto bg-[#F1F5F9] text-[#475569] border-0 gap-1 hover:bg-[#F1F5F9]">
                                        <Sparkles className="w-3 h-3" /> Generate
                                    </Badge>
                                )}
                            </div>
                            <p className="text-[10px] text-[#8A9A86] mt-2 inline-flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Updated {formatRelative(d.last_updated)}
                            </p>
                            {d.assignment?.notes && (
                                <p className="text-xs text-[#475569] mt-3 italic line-clamp-2">
                                    &ldquo;{d.assignment.notes}&rdquo;
                                </p>
                            )}
                        </Card>
                    ))}
                </div>
            )}

            <DrugDetailSheet drugId={activeId} onClose={() => setActiveId(null)} onChanged={load} />
        </div>
    );
}
